import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateFacility, fetchFacilityById, createFacility } from '../../redux/thunks/facilitiesThunks';
import { selectError, clearError } from '../../redux/slices/facilitiesSlice';
import DepartmentManagement from './DepartmentManagement';
import FacilityBillingTab from './FacilityBillingTab';
import FacilityInventoryTab from './FacilityInventoryTab';
import FacilitySubscriptionTab from './FacilitySubscriptionTab';
import FacilityStatementsTab from './FacilityStatementsTab';
import { EncounterType, UserRole } from '../../types/enums';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { getAllFacilities } from '../../services/facilityService';
import { getFacilityEncounterTypes } from '../../services/facilityPricingService';
import { useUserData } from '../../hooks/useUserData';
import { useSelector as useReduxSelector } from 'react-redux';
import { selectUserFacilities } from '../../redux/slices/facilitiesSlice';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';

// Valid facility type values (lowercase)
const VALID_FACILITY_TYPES = ['hospital', 'clinic', 'orthopedic_clinic', 'sports_medicine', 'laboratory', 'pharmacy', 'rehabilitation_center', 'surgical_center', 'urgent_care', 'other'];

const FacilityForm = () => {
  const { id: urlId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const groupId = searchParams.get('groupId');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useUserData();
  const userFacilities = useReduxSelector(selectUserFacilities);
  
  const facility = useSelector((state) => state.facilities.selectedFacility);
  const loading = useSelector((state) => state.facilities.loading);
  const error = useSelector(selectError);
  
  const facilityId = urlId;
  const isEditing = !!facilityId;

  // Listen for default facility changes and navigate to the new facility
  useEffect(() => {
    const handleFacilityChange = (event) => {
      const { facilityId: newFacilityId } = event.detail;
      
      // Only navigate if we're editing a facility and the new facility is different
      if (isEditing && newFacilityId && newFacilityId !== facilityId) {
        // Preserve the current path structure (e.g., /admin/facilities/:id)
        const basePath = location.pathname.replace(facilityId, '');
        navigate(`${basePath}${newFacilityId}`, { replace: true });
      }
    };

    window.addEventListener('defaultFacilityChanged', handleFacilityChange);
    return () => {
      window.removeEventListener('defaultFacilityChanged', handleFacilityChange);
    };
  }, [isEditing, facilityId, navigate, location.pathname]);
  
  const [activeTab, setActiveTab] = useState(0);
  const [availableFacilities, setAvailableFacilities] = useState([]);
  const [copyFromFacility, setCopyFromFacility] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'hospital',
    location: '',
    capacity: '',
    contact: '',
    phone: '',
    email: '',
    directAddress: '', // Direct Protocol address for health information exchange
    services: '',
    status: 'active',
    encounterTypes: []
  });

  const { isEnabled: surgicalEnabled } = useFeatureFlag('surgical', facilityId);
  const userRoles = user?.roles || [user?.role].filter(Boolean);
  const isAdmin = userRoles.includes(UserRole.ADMIN);

  // Load facilities for "Copy From" dropdown - defer this to not block initial render
  useEffect(() => {
    // Use requestIdleCallback or setTimeout to defer non-critical loading
    const loadFacilities = async () => {
      try {
        let facilities = [];
        if (userFacilities && userFacilities.length > 0) {
          // Prefer cached facilities from redux (instant)
          facilities = userFacilities;
        } else if (isAdmin) {
          // Admin can see all facilities - load async
          facilities = await getAllFacilities();
        }
        // Exclude current facility from the list
        const filtered = facilities.filter(f => f.id !== facilityId);
        setAvailableFacilities(filtered);
      } catch (err) {
        console.error('Error loading facilities:', err);
      }
    };
    
    // Defer loading to not block the initial render
    const timeoutId = setTimeout(loadFacilities, 100);
    return () => clearTimeout(timeoutId);
  }, [facilityId, isAdmin, userFacilities]);

  useEffect(() => {
    if (isEditing && facilityId) {
      dispatch(fetchFacilityById(facilityId));
    }
    return () => dispatch(clearError());
  }, [dispatch, facilityId, isEditing]);

  useEffect(() => {
    if (facility) {
      // Normalize type to lowercase and validate against available options
      const normalizedType = facility.type?.toLowerCase() || 'hospital';
      const validType = VALID_FACILITY_TYPES.includes(normalizedType) ? normalizedType : 'hospital';
      setFormData({
        name: facility.name || '',
        type: validType,
        location: facility.location || '',
        capacity: facility.capacity || '',
        contact: facility.contact || '',
        phone: facility.phone || '',
        email: facility.email || '',
        directAddress: facility.directAddress || '',
        services: facility.services || '',
        status: facility.status || 'active',
        encounterTypes: facility.encounterTypes || []
      });
    }
  }, [facility]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    dispatch(clearError());
    setSaveSuccess(null);
    
    try {
      let result;
      if (isEditing) {
        result = await dispatch(updateFacility({ id: facilityId, ...formData }));
      } else {
        const facilityData = { ...formData };
        if (groupId) facilityData.groupId = groupId;
        result = await dispatch(createFacility(facilityData));
      }
      
      if (!result.error) {
        setSaveSuccess('Facility saved successfully');
        if (!isEditing) {
          // Navigate to the new facility's edit page
          navigate(`/admin/facilities/${result.payload.id}`);
        }
      }
    } catch (err) {
      console.error('Failed to save facility:', err);
    }
  };

  const handleBack = () => {
    navigate('/admin/facility-groups');
  };

  const toggleEncounterType = (type) => {
    setFormData(prev => ({
      ...prev,
      encounterTypes: prev.encounterTypes.includes(type)
        ? prev.encounterTypes.filter(t => t !== type)
        : [...prev.encounterTypes, type]
    }));
  };

  // Copy encounter types from another facility
  const handleCopyEncounterTypes = async (sourceFacilityId) => {
    if (!sourceFacilityId) return;
    try {
      const data = await getFacilityEncounterTypes(sourceFacilityId);
      if (data.encounterTypes && data.encounterTypes.length > 0) {
        setFormData(prev => ({
          ...prev,
          encounterTypes: data.encounterTypes
        }));
        setSaveSuccess(`Copied encounter types from ${data.facilityName}`);
      }
    } catch (err) {
      console.error('Error copying encounter types:', err);
    }
    setCopyFromFacility('');
  };

  // Check if form is valid
  const isFormValid = formData.name.trim() && 
                     formData.type && 
                     formData.location.trim() && 
                     formData.contact.trim() && 
                     formData.encounterTypes.length > 0;

  const encounterTypeOptions = [
    { value: EncounterType.OUTPATIENT_VISIT, label: 'Outpatient' },
    { value: EncounterType.EMERGENCY_VISIT, label: 'Emergency Visit' },
    { value: EncounterType.INPATIENT_ADMISSION, label: 'Inpatient' },
    { value: EncounterType.TELEHEALTH, label: 'Telehealth' },
    { value: EncounterType.OBSERVATION, label: 'Observation' },
    { value: 'homeHealth', label: 'Home Health' },
    ...(surgicalEnabled ? [{ value: EncounterType.SURGERY, label: 'Surgery' }] : []),
  ];

  const facilityTypeOptions = [
    { value: 'hospital', label: '🏥 Hospital' },
    { value: 'clinic', label: '🏢 Clinic' },
    { value: 'orthopedic_clinic', label: '🦴 Orthopedic Clinic' },
    { value: 'sports_medicine', label: '🏃 Sports Medicine' },
    { value: 'laboratory', label: '🔬 Laboratory' },
    { value: 'pharmacy', label: '💊 Pharmacy' },
    { value: 'rehabilitation_center', label: '🏥 Rehabilitation Center' },
    { value: 'surgical_center', label: '🔪 Surgical Center' },
    { value: 'urgent_care', label: '🚑 Urgent Care' },
    { value: 'other', label: '📋 Other' },
  ];

  if (isEditing && !facility && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Render Basic Info Tab
  const renderBasicInfoTab = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
        Basic Information
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Facility Name"
            required
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            disabled={loading}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel>Facility Type</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              label="Facility Type"
              disabled={loading}
            >
              {facilityTypeOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Location / Address"
            required
            value={formData.location}
            onChange={(e) => handleInputChange('location', e.target.value)}
            disabled={loading}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Capacity"
            value={formData.capacity}
            onChange={(e) => handleInputChange('capacity', e.target.value)}
            disabled={loading}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Primary Contact"
            required
            value={formData.contact}
            onChange={(e) => handleInputChange('contact', e.target.value)}
            disabled={loading}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Phone Number"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            disabled={loading}
            placeholder="facility@example.com"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Direct Address (Health Info Exchange)"
            value={formData.directAddress}
            onChange={(e) => handleInputChange('directAddress', e.target.value)}
            disabled={loading}
            placeholder="facility@yourdomain.direct.com"
            helperText="Direct Protocol address for receiving external health messages"
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Services Offered"
            multiline
            rows={3}
            value={formData.services}
            onChange={(e) => handleInputChange('services', e.target.value)}
            disabled={loading}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
              label="Status"
              disabled={loading}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );

  // Render Encounter Types Tab
  const renderEncounterTypesTab = () => (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Encounter Types
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select the types of encounters this facility supports
          </Typography>
        </Box>
        
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Copy From Facility</InputLabel>
          <Select
            value={copyFromFacility}
            onChange={(e) => {
              setCopyFromFacility(e.target.value);
              if (e.target.value) {
                handleCopyEncounterTypes(e.target.value);
              }
            }}
            label="Copy From Facility"
            disabled={availableFacilities.length === 0}
            startAdornment={
              <InputAdornment position="start">
                <CopyIcon fontSize="small" />
              </InputAdornment>
            }
          >
            <MenuItem value="">
              <em>{availableFacilities.length === 0 ? 'No other facilities available' : 'Select facility...'}</em>
            </MenuItem>
            {availableFacilities.map(f => (
              <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      <Grid container spacing={2}>
        {encounterTypeOptions.map(typeObj => (
          <Grid item xs={12} sm={6} md={4} key={typeObj.value}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.encounterTypes.includes(typeObj.value)}
                  onChange={() => toggleEncounterType(typeObj.value)}
                  disabled={loading}
                />
              }
              label={typeObj.label}
            />
          </Grid>
        ))}
      </Grid>
      
      {formData.encounterTypes.length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Please select at least one encounter type
        </Alert>
      )}
    </Box>
  );

  // Render Departments Tab
  const renderDepartmentsTab = () => (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Departments & Rooms
        </Typography>
      </Box>
      
      <DepartmentManagement 
        facilityId={facilityId}
        availableFacilities={availableFacilities}
      />
    </Box>
  );

  // Render Billing Tab
  const renderBillingTab = () => (
    <FacilityBillingTab 
      facilityId={facilityId}
      availableFacilities={availableFacilities}
    />
  );

  // Render Inventory Tab
  const renderInventoryTab = () => (
    <FacilityInventoryTab facilityId={facilityId} />
  );

  // Render Subscription Tab (Admin only)
  const renderSubscriptionTab = () => (
    <FacilitySubscriptionTab facilityId={facilityId} />
  );

  // Render Statements Tab (Admin only)
  const renderStatementsTab = () => (
    <FacilityStatementsTab 
      facilityId={facilityId} 
      facilityName={facility?.name}
    />
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '16px',
          py: 1.5,
          px: 3,
          mb: 3,
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack} sx={{ color: 'white' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
              {isEditing ? (facility?.name || 'Edit Facility') : 'Create New Facility'}
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              {isEditing 
                ? 'Manage facility settings, departments, and billing configuration'
                : 'Add a new facility to your organization'
              }
            </Typography>
          </Box>
        </Box>
        
        {(activeTab === 0 || activeTab === 1) && (
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading || !isFormValid}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)'
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.5)'
              }
            }}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
          {error}
        </Alert>
      )}
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveSuccess(null)}>
          {saveSuccess}
        </Alert>
      )}

      {/* Tabbed Content */}
      <Card sx={{ borderRadius: '16px' }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 56,
              textTransform: 'none',
              fontWeight: 500
            }
          }}
        >
          <Tab label="Basic Info" />
          <Tab label="Encounter Types" />
          <Tab label="Departments & Rooms" disabled={!isEditing} />
          <Tab label="Billing" disabled={!isEditing} />
          <Tab label="Inventory" disabled={!isEditing} />
          {isAdmin && <Tab label="Subscription" disabled={!isEditing} />}
          {isAdmin && <Tab label="Statements" disabled={!isEditing} />}
        </Tabs>
        
        <CardContent sx={{ p: 0 }}>
          {activeTab === 0 && renderBasicInfoTab()}
          {activeTab === 1 && renderEncounterTypesTab()}
          {activeTab === 2 && renderDepartmentsTab()}
          {activeTab === 3 && renderBillingTab()}
          {activeTab === 4 && renderInventoryTab()}
          {isAdmin && activeTab === 5 && renderSubscriptionTab()}
          {isAdmin && activeTab === 6 && renderStatementsTab()}
        </CardContent>
      </Card>
    </Box>
  );
};

export default FacilityForm;
